import { memo } from "react";
import { NavLink } from "react-router-dom";
import { Menu, Search } from "lucide-react";
import { selectStatus, selectUser, useAuth } from "../stores/auth";
import { useUiStore } from "../stores/ui";
import { useTokenDrawer } from "../stores/token-drawer";
import { useMediaQuery } from "../lib/useMediaQuery";
import { LiveRibbon } from "./LiveRibbon";
import { useSpreadStream } from "../lib/useSpreadStream";
import { C } from "../lib/colors";

const MOBILE_BREAKPOINT = "(max-width: 767px)";

/**
 * Thin top bar (40 px). Holds:
 *
 *   * mobile-only hamburger button on the left (opens the sidebar drawer)
 *   * empty middle slot (placeholder for future theme / language /
 *     telegram link / search controls)
 *   * AuthWidget on the right (Sign in pill / Profile chip)
 *
 * On desktop the hamburger is hidden and the sidebar is always visible.
 * On mobile the bar stays visible but the sidebar is overlay-only.
 */
export const TopBar = memo(function TopBar() {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const setMobileOpen = useUiStore((s) => s.setMobileSidebar);

  // Keep /ws/spreads connected app-wide: TopBar lives in the persistent Layout
  // (mounted on every route), so the global LiveRibbon stays accurate when the
  // user navigates AWAY from the Spreads tab. Previously the stream was mounted
  // only by the Spreads page → leaving it dropped the WS and the ribbon flipped
  // to a misleading "OFFLINE … last update 17h ago".
  useSpreadStream();

  return (
    <div
      className="flex items-center"
      style={{
        height: 40,
        padding: "0 16px",
        borderBottom: `1px solid ${C.border}`,
        background: C.surface,
        gap: 8,
        flexShrink: 0,
      }}
    >
      {isMobile && (
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Open navigation"
          className="cursor-pointer transition-colors"
          style={{
            width: 32,
            height: 32,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#B4BCD2",
            background: "transparent",
            border: `1px solid ${C.border}`,
            borderRadius: 4,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "#E9ECEF";
            e.currentTarget.style.background = "rgba(255,255,255,0.04)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "#B4BCD2";
            e.currentTarget.style.background = "transparent";
          }}
        >
          <Menu size={16} strokeWidth={2} />
        </button>
      )}

      {/* Middle slot — live activity ribbon (added 2026-05-23 as part
          of the landing-dashboard brand sync). Derived from the
          existing ``useSpreadsStore`` snapshot, no new endpoint.
          Mobile auto-collapses to "Live · N spreads" via Tailwind
          ``hidden md:inline`` classes inside the ribbon itself. */}
      <LiveRibbon />

      <TokenInfoButton />
      <AuthWidget />
    </div>
  );
});

/**
 * Token Info launcher (#105). Opens the global right-side ``TokenInfoDrawer``
 * with an empty query so the user can search any token by symbol / name — the
 * same drawer Spreads / Dashboard rows open. Replaces the old left-sidebar
 * "Token Info" nav entry. Label collapses to the icon on mobile.
 */
function TokenInfoButton() {
  return (
    <button
      type="button"
      onClick={() => useTokenDrawer.getState().openFor("")}
      aria-label="Token Info search"
      title="Token Info — search any token by symbol or name"
      className="flex items-center gap-2 cursor-pointer border border-borderHi bg-raised px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-text2 transition-colors hover:border-mint/40 hover:text-mint"
    >
      <Search size={13} strokeWidth={2} />
      <span className="hidden sm:inline">Token</span>
    </button>
  );
}

/**
 * Compact auth indicator. Mirrors the previous header version that
 * lived inside ``Layout.tsx`` — moved here so the top bar owns it
 * exclusively and the sidebar stays focused on navigation.
 */
function AuthWidget() {
  const status = useAuth(selectStatus);
  const user = useAuth(selectUser);

  if (status === "loading") {
    return (
      <div className="font-mono text-[10px] uppercase tracking-wider text-text4">
        ···
      </div>
    );
  }

  if (!user) {
    return (
      <NavLink
        to="/login"
        className="border border-borderHi bg-raised px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-text2 transition-colors hover:border-mint/40 hover:text-mint"
      >
        Sign in
      </NavLink>
    );
  }

  const isAdmin = user.role === "admin";

  return (
    <NavLink
      to="/profile"
      className={({ isActive }) =>
        `flex items-center gap-2 border px-2.5 py-1 transition-colors ${
          isActive
            ? "border-borderHi bg-bg"
            : "border-border bg-raised hover:border-borderHi"
        }`
      }
      title="Account"
    >
      <span className="text-[11px] font-semibold text-text2">
        {user.display_name}
      </span>
      {isAdmin ? (
        <span className="border border-amber/50 bg-amber/10 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.2em] text-amber">
          Admin
        </span>
      ) : (
        <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-text4">
          User
        </span>
      )}
    </NavLink>
  );
}
